import { Alert, Button, Stack } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import ExperienceFormModal from "./_sections/experiences/ExperienceForm";
import DialogBox from "components/lib-ui/DialogBox";
import ExperienceCard from "./_sections/experiences/ExperienceCard";
import { CatButton } from "assets/styled-components/CatButton";
import AddIcon from "@mui/icons-material/Add";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";

const hasExperienceStartDate = (experience) => {
    const startAt = experience?.startAt;

    if (typeof startAt === "string") {
        return startAt.trim() !== "";
    }

    return startAt !== null && startAt !== undefined;
};

const normalizeMonthValue = (value) => {
    if (typeof value !== "string") {
        return null;
    }

    const trimmedValue = value.trim();

    if (!trimmedValue) {
        return null;
    }

    return trimmedValue.slice(0, 7);
};

const hasInvalidExperienceDateRange = (experience) => {
    const startAt = normalizeMonthValue(experience?.startAt);
    const endAt = normalizeMonthValue(experience?.endAt);

    if (!startAt || !endAt) {
        return false;
    }

    return endAt <= startAt;
};

export default function ExperienceForm({ onSubmit, locationTitle = "" }) {
    const {
        handleSubmit,
        clearErrors,
        setError,
        formState: { errors, isSubmitted },
    } = useForm();
    const [dialogOpen, setDialogOpen] = useState(false);
    const experiencesErrorRef = useRef(null);

    const experiencesFromAPI = useSelector(
        (state) => state.currentCandidateAI.experiences,
    );
    const [experiences, setExperiences] = useState(
        experiencesFromAPI?.data || [],
    );
    const hasMissingExperienceStartDate = experiences.some(
        (experience) => !hasExperienceStartDate(experience),
    );
    const hasExperienceWithInvalidDateRange = experiences.some((experience) =>
        hasInvalidExperienceDateRange(experience),
    );
    const experienceStartDateErrorMessage =
        "Each experience must include a start date before you can submit.";
    const experienceDateRangeErrorMessage =
        "Each experience end date must be later than its start date. The same month is not allowed.";
    const experiencesErrorMessage = [
        hasMissingExperienceStartDate ? experienceStartDateErrorMessage : null,
        hasExperienceWithInvalidDateRange
            ? experienceDateRangeErrorMessage
            : null,
    ]
        .filter(Boolean)
        .join(" ");

    useEffect(() => {
        if (!isSubmitted) {
            return;
        }

        if (experiencesErrorMessage) {
            setError("root.experiences", {
                type: "manual",
                message: experiencesErrorMessage,
            });
            return;
        }

        clearErrors("root.experiences");
    }, [clearErrors, experiencesErrorMessage, isSubmitted, setError]);

    useEffect(() => {
        if (
            !errors.root?.experiences?.message ||
            !experiencesErrorRef.current
        ) {
            return;
        }

        experiencesErrorRef.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
        experiencesErrorRef.current.focus();
    }, [errors.root?.experiences?.message]);

    const onExperienceFormSubmit = () => {
        if (experiencesErrorMessage) {
            setError("root.experiences", {
                type: "manual",
                message: experiencesErrorMessage,
            });
            return;
        }

        clearErrors("root.experiences");
        onSubmit(experiences);
    };
    return (
        <Stack
            onSubmit={handleSubmit(onExperienceFormSubmit)}
            component="form"
            sx={{ position: "relative" }}
        >
            {errors.root?.experiences?.message && (
                <Alert
                    ref={experiencesErrorRef}
                    severity="error"
                    tabIndex={-1}
                    sx={{ mt: 0, mb: 2 }}
                >
                    {errors.root.experiences.message}
                </Alert>
            )}
            {experiences.length === 0 && <p>No experiences added yet.</p>}
            {experiences.length > 0 && (
                <>
                    <Stack
                        spacing={2}
                        sx={{
                            width: "100%",
                            pb: 2,
                        }}
                    >
                        {experiences.map((data, index) => (
                            <ExperienceCard
                                key={index}
                                experience={data}
                                setExperiences={setExperiences}
                            />
                        ))}
                    </Stack>
                </>
            )}
            <Stack
                direction="row"
                spacing={2}
                sx={{
                    mt: 2,
                    position: "sticky",
                    bottom: "0",
                    right: "0",
                    zIndex: 10,
                    justifyContent: "space-between",
                    backgroundColor: "white",
                    margin: "0 -24px",
                    padding: "8px 16px",
                    boxShadow: "0 -2px 4px rgba(0,0,0,0.1)",
                    alignContent: "center",
                }}
            >
                <Button
                    sx={{ borderRadius: "50px", padding: "10px 30px" }}
                    size="medium"
                    color="primary"
                    variant="contained"
                    onClick={() => setDialogOpen(true)}
                >
                    <AddIcon sx={{ marginRight: "5px" }} />
                    Add new experience
                </Button>
                <CatButton
                    type="submit"
                    size="medium"
                    variant="contained"
                    sx={{ width: "fit-content" }}
                >
                    Submit Experiences
                </CatButton>
            </Stack>
            <DialogBox
                isDialogOpen={dialogOpen}
                setIsDialogOpen={setDialogOpen}
                title="Edit candidate experience"
                floatCancel={true}
                showActionButtons={false}
            >
                <ExperienceFormModal
                    locationTitle={locationTitle}
                    edit={false}
                    setIsDialogOpen={setDialogOpen}
                    setExperiences={setExperiences}
                />
            </DialogBox>
        </Stack>
    );
}
